export const baseThemeOptions = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 750,
      md: 1000,
      lg: 1240,
      xl: 1920,
    },
  },
  components: {
    MuiAvatar: {
      styleOverrides: {
        root: {
          fontSize: 14,
          fontWeight: 600,
          letterSpacing: 0,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "50px", // Pill shape for modern look
          fontWeight: 700,
          fontFamily: '"Inter", sans-serif',
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 20px -4px rgba(13, 148, 136, 0.4)", // Teal shadow
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        containedPrimary: {
          background: "linear-gradient(135deg, #0D9488 0%, #0F766E 100%)", // Teal gradient
          "&:hover": {
            background: "linear-gradient(135deg, #14B8A6 0%, #0D9488 100%)",
          },
        },
        outlinedPrimary: {
          borderColor: "#0D9488",
          color: "#0D9488",
          "&:hover": {
            backgroundColor: "rgba(13, 148, 136, 0.08)",
            borderColor: "#0F766E",
          },
        },
        sizeSmall: {
          padding: "8px 20px",
          fontSize: "0.875rem",
        },
        sizeMedium: {
          padding: "12px 28px",
          fontSize: "0.9375rem",
        },
        sizeLarge: {
          padding: "16px 36px",
          fontSize: "1.0625rem",
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: false,
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0, // No elevation by default, use border instead
      },
      styleOverrides: {
        root: {
          borderRadius: "16px", // MUI dashboard standard
          border: "1px solid",
          borderColor: "#E0E0E0", // neutral[300]
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            borderColor: "#BDBDBD", // neutral[400]
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.08)",
            transform: "translateY(-2px)",
          },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: "24px", // MUI dashboard standard
          "&:last-child": {
            paddingBottom: "24px",
          },
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: "h6",
          fontWeight: 600, // Slightly bolder for hierarchy
        },
        subheaderTypographyProps: {
          variant: "body2",
        },
      },
      styleOverrides: {
        root: {
          padding: "24px 24px 16px", // MUI dashboard standard
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: {
          borderRadius: "6px",
          "&.Mui-checked": {
            color: "#0D9488",
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          borderRadius: "12px",
        },
        colorPrimary: {
          backgroundColor: "rgba(13, 148, 136, 0.1)",
          color: "#0D9488",
          border: "1px solid rgba(13, 148, 136, 0.2)",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
        },
        html: {
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
          display: "flex",
          flexDirection: "column",
          minHeight: "100%",
          width: "100%",
          scrollBehavior: "smooth",
        },
        body: ({ theme }) => ({
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          minHeight: "100%",
          width: "100%",
          fontFamily: '"Inter", sans-serif',
          backgroundColor: theme.palette.background.default,
        }),
        "#__next": {
          display: "flex",
          flex: "1 1 auto",
          flexDirection: "column",
          height: "100%",
          width: "100%",
        },
        "#nprogress": {
          pointerEvents: "none",
        },
        "#nprogress .bar": {
          background: "linear-gradient(90deg, #0D9488 0%, #2DD4BF 100%)", // Teal gradient
          height: 4,
          left: 0,
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 2000,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: "50%", // Circular - MUI dashboard standard
          padding: 8,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            backgroundColor: "rgba(13, 148, 136, 0.08)",
          },
        },
        sizeSmall: {
          padding: 4,
        },
        sizeMedium: {
          padding: 8,
        },
        sizeLarge: {
          padding: 12,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          overflow: "hidden",
          backgroundColor: "rgba(13, 148, 136, 0.1)",
        },
        bar: {
          backgroundColor: "#0D9488",
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "none",
      },
      styleOverrides: {
        root: {
          textDecoration: "none",
          transition: "color 0.2s",
          "&:hover": {
            color: "#0D9488",
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          marginRight: "16px",
          minWidth: "unset",
          color: "#64748B",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "16px",
          transition: "all 0.2s",
          backgroundColor: "#FFFFFF",
          "& fieldset": {
            borderColor: "#E2E8F0",
          },
          "&.Mui-focused": {
            boxShadow: "0 0 0 4px rgba(13, 148, 136, 0.1)",
            "& fieldset": {
              borderColor: "#0D9488",
              borderWidth: "1px",
            },
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0D9488",
          },
        },
        input: {
          fontWeight: 500,
          padding: "16px 20px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        elevation1: {
          boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.05)",
        },
      },
    },
    MuiPopover: {
      defaultProps: {
        elevation: 16,
      },
    },
    MuiRadio: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        root: {
          "&.Mui-checked": {
            color: "#0D9488",
          },
        },
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: "primary",
      },
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: "#0D9488",
            "& + .MuiSwitch-track": {
              backgroundColor: "#0D9488",
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: "0.9375rem",
          fontWeight: 600,
          textTransform: "none",
          minWidth: "auto",
          padding: "12px 24px",
          borderRadius: "12px",
          transition: "all 0.2s",
          marginRight: "8px",
          "&:hover": {
            backgroundColor: "rgba(13, 148, 136, 0.08)",
            color: "#0D9488",
          },
          "&.Mui-selected": {
            color: "#0D9488",
            backgroundColor: "rgba(13, 148, 136, 0.1)",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "20px 24px",
          fontSize: "0.875rem",
          borderBottom: "1px solid #F1F5F9",
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-root": {
            fontSize: "0.75rem",
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: "#64748B",
            backgroundColor: "#F8FAFC",
          },
        },
      },
    },
  },
  direction: "ltr",
  shape: {
    borderRadius: 16,
  },
  typography: {
    fontFamily: '"Inter", "Poppins", "Segoe UI", "Roboto", "Helvetica Neue", "Arial", sans-serif',
    button: {
      fontWeight: 700,
      fontFamily: '"Inter", sans-serif',
    },
    body1: {
      fontSize: "1rem",
      fontWeight: 400,
      lineHeight: 1.6,
      fontFamily: '"Inter", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
    body2: {
      fontSize: "0.875rem",
      fontWeight: 400,
      lineHeight: 1.57,
      fontFamily: '"Inter", sans-serif',
      // Color removed - will use theme.palette.text.secondary
    },
    subtitle1: {
      fontSize: "1.125rem",
      fontWeight: 600,
      lineHeight: 1.5,
      fontFamily: '"Inter", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
    subtitle2: {
      fontSize: "0.875rem",
      fontWeight: 600,
      lineHeight: 1.57,
      fontFamily: '"Inter", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
    overline: {
      fontSize: "0.75rem",
      fontWeight: 700,
      letterSpacing: "1px",
      lineHeight: 2.5,
      textTransform: "uppercase",
      fontFamily: '"Inter", sans-serif',
      // Color removed - will use theme.palette.text.secondary
    },
    caption: {
      fontSize: "0.75rem",
      fontWeight: 400,
      lineHeight: 1.66,
      fontFamily: '"Inter", sans-serif',
      // Color removed - will use theme.palette.text.secondary
    },
    h1: {
      fontWeight: 800,
      fontSize: "3.5rem",
      lineHeight: 1.2,
      fontFamily: '"Poppins", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
    h2: {
      fontWeight: 800,
      fontSize: "3rem",
      lineHeight: 1.2,
      fontFamily: '"Poppins", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
    h3: {
      fontWeight: 700,
      fontSize: "2.25rem",
      lineHeight: 1.2,
      fontFamily: '"Poppins", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
    h4: {
      fontWeight: 700,
      fontSize: "2rem",
      lineHeight: 1.2,
      fontFamily: '"Poppins", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
    h5: {
      fontWeight: 700,
      fontSize: "1.5rem",
      lineHeight: 1.2,
      fontFamily: '"Poppins", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
    h6: {
      fontWeight: 700,
      fontSize: "1.125rem",
      lineHeight: 1.2,
      fontFamily: '"Poppins", sans-serif',
      // Color removed - will use theme.palette.text.primary
    },
  },
  zIndex: {
    appBar: 1200,
    drawer: 1100,
  },
};
